"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useAuth } from "@/context/AuthContext";
import {
  fetchOrders as apiFetchOrders,
  type PWOrder,
} from "@/lib/api/customer";

export type { PWOrder };

/** Coalesce rapid auto-fetches (React Strict Mode + auth hydration). */
const AUTO_FETCH_DEDUPE_MS = 2500;

type OrderContextValue = {
  orders: PWOrder[];
  loading: boolean;
  refreshOrders: () => Promise<void>;
};

const OrderContext = createContext<OrderContextValue | null>(null);

export function OrderProvider({ children }: { children: ReactNode }) {
  const { token, patientId, isAuthenticated, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<PWOrder[]>([]);
  const [loading, setLoading] = useState(false);

  const lastAutoFetchRef = useRef<{ key: string; at: number } | null>(null);

  const pullOrders = useCallback(async () => {
    if (!token || !patientId) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await apiFetchOrders(token, patientId, 50);
    if (res.ok) {
      setOrders(res.data.orders);
    }
    setLoading(false);
  }, [token, patientId]);

  const pullOrdersRef = useRef(pullOrders);
  pullOrdersRef.current = pullOrders;

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !token || !patientId) {
      setOrders([]);
      return;
    }

    const key = `${patientId}\u0000${token}`;
    const now = Date.now();
    const prev = lastAutoFetchRef.current;
    if (prev && prev.key === key && now - prev.at < AUTO_FETCH_DEDUPE_MS) {
      return;
    }
    lastAutoFetchRef.current = { key, at: now };

    void pullOrdersRef.current();
  }, [authLoading, isAuthenticated, token, patientId]);

  const refreshOrders = useCallback(async () => {
    lastAutoFetchRef.current = null;
    await pullOrders();
  }, [pullOrders]);

  return (
    <OrderContext.Provider value={{ orders, loading, refreshOrders }}>
      {children}
    </OrderContext.Provider>
  );
}

export function useOrders() {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error("useOrders must be used within OrderProvider");
  return ctx;
}
